import { useState, useEffect } from 'react';
import { Search, Copy, Eye, Plus, Loader2, Compass, ThumbsUp, ThumbsDown, MessageCircle, Download, Trash2, Pencil, Check, X, Share2, Radio, Tv, Users, BookOpen, Play, Sword, RefreshCw, FolderOpen, Crown, Lock, Globe, Zap, Gift, ExternalLink, Send, ChevronDown, ChevronRight, ArrowLeft, UserPlus, Mail } from 'lucide-react';
import { deckService, authService, commentService, roleService, sparksService, DeckRecord, CommentRecord, supabase } from '../lib/supabase';
import { SaveDeckModal } from './SaveDeckModal';
import { DeckPlaytester } from './DeckPlaytester';
import { ArenaTracker } from './ArenaTracker';
import { fetchCardData, getCardImageUrl } from '../lib/scryfall';
import { ManaSymbol } from './ManaSymbol';
import { MetaPanel } from './MetaPanel';
import { SubNavDropdown } from './SubNavDropdown';
import type { Archetype } from '../lib/metaData';
import type { Card } from '../App';

// Streamer interface for live streamers section
interface Streamer {
    id: string;
    username: string;
    displayName: string;
    twitchUrl: string;
    viewerCount: number;
    isLive: boolean;
    lastSeen: string;
    streamTitle?: string;
    gameName?: string;
    thumbnailUrl?: string;
    profileImageUrl?: string;
}

interface DiscoverTabProps {
    deck: Card[];
    deckName: string;
    deckSize: number;
    landCount: number;
    manaCurve: number;
    onLoadDeck?: (cards: Record<string, number>, lands: Record<string, number>, sideboard: Record<string, number>, name: string) => void;
    onNavigateToSession?: (deckData: { cards: Array<{ name: string; quantity: number; imageUrl?: string; cmc: number; isLand: boolean; colors: string[] }>; deckName: string }) => void;
}

export function DiscoverTab({ deck, deckName, deckSize, landCount, manaCurve, onLoadDeck, onNavigateToSession }: DiscoverTabProps) {
    const [user, setUser] = useState<any>(null);
    const [decks, setDecks] = useState<DeckRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    // Discover mode: 'decks', 'streamers', or 'mydecks'
    const [discoverMode, setDiscoverMode] = useState<'decks' | 'streamers' | 'mydecks' | 'meta'>('decks');
    const [streamers, setStreamers] = useState<Streamer[]>([]);
    const [offlineStreamers, setOfflineStreamers] = useState<Streamer[]>([]);
    const [liveViewers, setLiveViewers] = useState(0);

    // Deck detail view
    const [selectedDeck, setSelectedDeck] = useState<DeckRecord | null>(null);
    const [comments, setComments] = useState<CommentRecord[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loadingComments, setLoadingComments] = useState(false);

    // Edit mode for deck owners
    const [editMode, setEditMode] = useState(false);
    const [editName, setEditName] = useState('');
    const [editFormat, setEditFormat] = useState('');

    // Streamer registration
    const [twitchUsername, setTwitchUsername] = useState('');
    const [registrationLoading, setRegistrationLoading] = useState(false);
    const [isRegisteredStreamer, setIsRegisteredStreamer] = useState(false);
    const [myLiveStatus, setMyLiveStatus] = useState(false);

    // Overlay personalization
    const [overlayName, setOverlayName] = useState('');

    // Embedded Twitch player / Brewlette panel
    const [watchingStreamer, setWatchingStreamer] = useState<Streamer | null>(null);
    const [streamerActiveDeck, setStreamerActiveDeck] = useState<any>(null);
    const [streamerQueue, setStreamerQueue] = useState<any[]>([]);
    const [streamerScore, setStreamerScore] = useState<{ wins: number; losses: number; draws: number } | null>(null);
    const [streamerPanelLoading, setStreamerPanelLoading] = useState(false);
    const [submittingDeck, setSubmittingDeck] = useState(false);

    // Playtester state
    const [showPlaytester, setShowPlaytester] = useState(false);
    const [playtesterDeck, setPlaytesterDeck] = useState<DeckRecord | null>(null);
    const [playtesterCards, setPlaytesterCards] = useState<Array<{
        name: string;
        quantity: number;
        imageUrl?: string;
        cmc: number;
        isLand: boolean;
        colors: string[];
    }>>([]);
    const [playtesterLoading, setPlaytesterLoading] = useState(false);
    const [playtesterMode, setPlaytesterMode] = useState<'goldfish' | 'session'>('goldfish');

    // User role for mod actions
    const [userRole, setUserRole] = useState<string>('user');

    // Voting state (DB-backed)
    const [userVotes, setUserVotes] = useState<Record<string, 1 | -1>>({});
    const [sparkBalance, setSparkBalance] = useState<number>(0);

    // Spark toast notifications
    const [sparkToasts, setSparkToasts] = useState<Array<{ id: string; message: string; amount: number; type: 'earn' | 'spend' }>>([]);
    const [canClaimDaily, setCanClaimDaily] = useState(false);
    const [claimingDaily, setClaimingDaily] = useState(false);

    // Helper to show spark toast
    const showSparkToast = (message: string, amount: number, type: 'earn' | 'spend' = 'earn') => {
        const id = Date.now().toString();
        setSparkToasts(prev => [...prev, { id, message, amount, type }]);
        setTimeout(() => {
            setSparkToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    };

    // [+] Add Deck popover state
    const [showAddDeck, setShowAddDeck] = useState(false);
    const [newDeckTitle, setNewDeckTitle] = useState('Untitled Deck');
    const [newDeckFormat, setNewDeckFormat] = useState('standard');
    const [newDeckCommander, setNewDeckCommander] = useState('');
    const [newDeckVisibility, setNewDeckVisibility] = useState<'public' | 'private'>('public');

    // My Decks state
    const [myDecks, setMyDecks] = useState<DeckRecord[]>([]);
    const [myDecksLoading, setMyDecksLoading] = useState(false);

    // User Profile view state
    const [viewingUserProfile, setViewingUserProfile] = useState<{ id: string; name: string } | null>(null);
    const [profileDecks, setProfileDecks] = useState<DeckRecord[]>([]);
    const [profileDecksLoading, setProfileDecksLoading] = useState(false);

    // Format ordering for grouped display
    const FORMAT_ORDER = ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander', 'pauper', 'historic', 'explorer', 'limited'];
    const groupDecksByFormat = (deckList: DeckRecord[]) => {
        const groups: Record<string, DeckRecord[]> = {};
        for (const d of deckList) {
            const fmt = (d.format || 'other').toLowerCase();
            if (!groups[fmt]) groups[fmt] = [];
            groups[fmt].push(d);
        }
        // Sort by FORMAT_ORDER, unknowns at end
        return Object.entries(groups).sort(([a], [b]) => {
            const ai = FORMAT_ORDER.indexOf(a);
            const bi = FORMAT_ORDER.indexOf(b);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
    };

    const viewUserProfile = async (userId: string, authorName: string) => {
        setViewingUserProfile({ id: userId, name: authorName });
        setProfileDecksLoading(true);
        setDiscoverMode('decks'); // stay in decks mode but show profile overlay
        try {
            const userDecks = await deckService.getPublicDecksByUser(userId);
            setProfileDecks(userDecks);
        } catch (err) {
            console.error('Failed to load user decks:', err);
        } finally {
            setProfileDecksLoading(false);
        }
    };

    // Check auth state on mount
    useEffect(() => {
        authService.getCurrentUser().then(setUser);
        const { data: { subscription } } = authService.onAuthStateChange(setUser);
        return () => subscription.unsubscribe();
    }, []);

    // Check if user is a registered streamer
    useEffect(() => {
        if (user) {
            checkStreamerRegistration();
            // Get user role for mod actions
            roleService.getCurrentUserRole().then(setUserRole);
            // Load spark balance & check daily claim
            sparksService.getBalance().then(setSparkBalance);
            sparksService.canClaimDaily().then(setCanClaimDaily);

            // Auto-trigger signup bonus on first auth
            sparksService.awardSignupBonus().then(result => {
                if (result.success && result.sparks) {
                    showSparkToast(`Welcome to DECKSTR! Here's your signup bonus`, result.sparks, 'earn');
                    sparksService.getBalance().then(setSparkBalance);
                }
            });

            // Clean up any OAuth URL params
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('twitch_linked') === 'true' || urlParams.get('provider_linked')) {
                window.history.replaceState({}, '', window.location.pathname);
            }
        } else {
            setIsRegisteredStreamer(false);
        }
    }, [user]);

    const registerTwitchStreamer = async (username: string, displayName: string) => {
        if (!user) return;
        try {
            const { error } = await supabase.from('streamers').upsert({
                user_id: user.id,
                username: username.toLowerCase(),
                display_name: displayName,
                twitch_url: `https://www.twitch.tv/${username.toLowerCase()}`,
                is_live: false,
                viewer_count: 0
            }, { onConflict: 'user_id' });

            if (!error) {
                setIsRegisteredStreamer(true);
                setTwitchUsername(username.toLowerCase());
                alert("🎉 Your Twitch account is now linked! You'll appear in Discover Streamers when live.");
            }
        } catch (err) {
            console.error('Failed to register Twitch:', err);
        }
    };

    const checkStreamerRegistration = async () => {
        if (!user) return;
        try {
            const { data } = await supabase
                .from('streamers')
                .select('id, username, is_live')
                .eq('user_id', user.id)
                .single();

            if (data) {
                setIsRegisteredStreamer(true);
                setTwitchUsername(data.username);
                setMyLiveStatus(data.is_live || false);
            }
        } catch (err) {
            // Not registered, that's fine
        }
    };

    // Toggle streamer's live status manually
    const toggleMyLiveStatus = async () => {
        if (!user || !isRegisteredStreamer) return;

        const newStatus = !myLiveStatus;
        try {
            const { error } = await supabase
                .from('streamers')
                .update({ is_live: newStatus, viewer_count: newStatus ? 0 : 0 })
                .eq('user_id', user.id);

            if (!error) {
                setMyLiveStatus(newStatus);
                loadStreamers(); // Refresh the list
            }
        } catch (err) {
            console.error('Failed to toggle status:', err);
        }
    };

    const handleRegisterStreamer = async () => {
        if (!user || !twitchUsername.trim()) {
            alert('Please sign in and enter your Twitch username');
            return;
        }

        setRegistrationLoading(true);
        try {
            const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || twitchUsername;

            const { error } = await supabase.from('streamers').upsert({
                user_id: user.id,
                username: twitchUsername.toLowerCase().trim(),
                display_name: displayName,
                twitch_url: `https://www.twitch.tv/${twitchUsername.toLowerCase().trim()}`,
                is_live: false,
                viewer_count: 0
            }, { onConflict: 'user_id' });

            if (error) throw error;

            setIsRegisteredStreamer(true);
            alert("🎉 You're registered! When you go live, you'll appear in Discover Streamers.");
        } catch (err: any) {
            console.error('Registration failed:', err);
            alert(err.message || 'Registration failed');
        } finally {
            setRegistrationLoading(false);
        }
    };

    // Load decks
    useEffect(() => {
        loadDecks();
    }, []);

    // Load streamers and track live viewers using Realtime Presence
    useEffect(() => {
        // Subscribe to discover presence channel to count live viewers
        const channel = supabase.channel('discover-viewers', {
            config: { presence: { key: Math.random().toString(36).substring(7) } }
        });

        channel.on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState();
            setLiveViewers(Object.keys(state).length);
        });

        channel.subscribe(async (status) => {
            if (status === 'SUBSCRIBED') {
                await channel.track({ online_at: new Date().toISOString() });
            }
        });

        // Load registered streamers
        loadStreamers();

        return () => {
            channel.unsubscribe();
        };
    }, []);

    const [streamersLoading, setStreamersLoading] = useState(false);

    const loadStreamers = async (forceRefresh = false) => {
        setStreamersLoading(true);
        try {
            // Call edge function to refresh Twitch live status from API
            try {
                console.log('🔄 Calling update-streamers edge function...');
                const { data: fnData, error: fnError } = await supabase.functions.invoke('update-streamers');
                if (fnError) {
                    console.error('❌ Edge function error:', fnError);
                } else {
                    console.log('✅ Streamer status refreshed:', fnData);
                }
            } catch (fnErr: any) {
                console.error('❌ Edge function failed:', fnErr?.message || fnErr);
            }

            // Fetch ALL registered streamers
            const { data: allStreamers, error } = await supabase
                .from('streamers')
                .select('*')
                .order('viewer_count', { ascending: false });

            if (!error && allStreamers) {
                const mapStreamer = (s: any): Streamer => ({
                    id: s.id,
                    username: s.username,
                    displayName: s.display_name,
                    twitchUrl: s.twitch_url,
                    viewerCount: s.viewer_count || 0,
                    isLive: s.is_live,
                    lastSeen: s.last_seen,
                    streamTitle: s.stream_title,
                    gameName: s.game_name,
                    thumbnailUrl: s.thumbnail_url,
                    profileImageUrl: s.profile_image_url,
                });

                const online = allStreamers.filter(s => s.is_live).map(mapStreamer);
                const offline = allStreamers.filter(s => !s.is_live).map(mapStreamer);

                console.log(`🎬 ${online.length} online, ${offline.length} offline streamers`);
                setStreamers(online);
                setOfflineStreamers(offline);
            }
        } catch (err) {
            console.error('Failed to load streamers:', err);
        } finally {
            setStreamersLoading(false);
        }
    };

    // Load Brewlette panel data when a streamer is selected
    const loadStreamerPanel = async (streamer: Streamer) => {
        setStreamerPanelLoading(true);
        setStreamerActiveDeck(null);
        setStreamerQueue([]);
        setStreamerScore(null);
        try {
            const channelName = streamer.username.toLowerCase();
            const [deckRes, scoreRes, queueRes] = await Promise.all([
                supabase.from('channel_decks').select('*').eq('channel_name', channelName).maybeSingle(),
                supabase.from('channel_scores').select('*').eq('channel_name', channelName).maybeSingle(),
                supabase.from('queue_entries').select('*').eq('streamer_id', channelName).in('status', ['waiting', 'selected']).order('sparks', { ascending: false, nullsFirst: false }).order('created_at', { ascending: true }),
            ]);

            if (deckRes.data?.deck_data) {
                const dd = typeof deckRes.data.deck_data === 'string' ? JSON.parse(deckRes.data.deck_data) : deckRes.data.deck_data;
                setStreamerActiveDeck(dd);
            }
            if (scoreRes.data) {
                setStreamerScore({ wins: scoreRes.data.wins || 0, losses: scoreRes.data.losses || 0, draws: scoreRes.data.draws || 0 });
            }
            if (queueRes.data) {
                setStreamerQueue(queueRes.data);
            }
        } catch (err) {
            console.error('Failed to load streamer panel:', err);
        } finally {
            setStreamerPanelLoading(false);
        }
    };

    useEffect(() => {
        if (watchingStreamer) {
            loadStreamerPanel(watchingStreamer);
            const interval = setInterval(() => loadStreamerPanel(watchingStreamer), 15000);
            return () => clearInterval(interval);
        }
    }, [watchingStreamer]);

    // Submit deck to a streamer's Brewlette queue
    const handleSubmitToBrewlette = async () => {
        if (!user || !watchingStreamer) {
            setShowAuthModal(true);
            return;
        }
        const submitCost = 3;
        if (sparkBalance < submitCost) {
            showSparkToast('Not enough Sparks to submit', submitCost, 'spend');
            return;
        }
        if (!deck || deck.length === 0) {
            showSparkToast('No deck loaded to submit', 0, 'spend');
            return;
        }
        setSubmittingDeck(true);
        try {
            const displayNameStr = user.user_metadata?.display_name || user.user_metadata?.name || user.email?.split('@')[0] || 'Anonymous';
            const deckCards: Record<string, number> = {};
            const deckLands: Record<string, number> = {};
            const deckColors: string[] = [];
            let coverArt: string | null = null;
            for (const card of deck) {
                if (card.isLand) {
                    deckLands[card.name] = (deckLands[card.name] || 0) + card.quantity;
                } else {
                    deckCards[card.name] = (deckCards[card.name] || 0) + card.quantity;
                }
                if (card.colors) {
                    for (const c of card.colors) {
                        if (!deckColors.includes(c)) deckColors.push(c);
                    }
                }
                if (!coverArt && card.imageUrl) coverArt = card.imageUrl;
            }

            const { error } = await supabase.from('queue_entries').insert({
                streamer_id: watchingStreamer.username.toLowerCase(),
                deck_name: deckName || 'Unnamed Deck',
                deck_colors: deckColors,
                deck_cards: deckCards,
                deck_lands: deckLands,
                deck_art: coverArt,
                display_name: displayNameStr,
                twitch_user_id: user.id,
                sparks: 0,
                status: 'pending',
            });
            if (error) throw error;

            // Spend sparks
            const spendResult = await sparksService.spendSparks(submitCost, 'brewlette_submit', watchingStreamer.username);
            if (spendResult.balance !== undefined) {
                setSparkBalance(spendResult.balance);
            }
            showSparkToast(`Deck submitted to ${watchingStreamer.displayName}'s queue!`, submitCost, 'spend');

            // Award first brew bonus
            const brewResult = await sparksService.awardFirstBrew(watchingStreamer.username);
            if (brewResult.success && brewResult.sparks) {
                showSparkToast(`First brew at ${watchingStreamer.displayName}'s channel!`, brewResult.sparks, 'earn');
                sparksService.getBalance().then(setSparkBalance);
            }

            // Check "On the House" bonus (+5 after first 5 spent)
            const onTheHouse = await sparksService.checkOnTheHouse();
            if (onTheHouse.awarded) {
                showSparkToast('On the house! Here\'s 5 more Sparks 🎉', 5, 'earn');
                if (onTheHouse.balance !== undefined) setSparkBalance(onTheHouse.balance);
            }

            loadStreamerPanel(watchingStreamer);
        } catch (err) {
            console.error('Failed to submit to Brewlette:', err);
            showSparkToast('Failed to submit deck', 0, 'spend');
        } finally {
            setSubmittingDeck(false);
        }
    };

    // Check for deck ID in hash route (for shared links)
    // Format: #discover-decks/author_name/deck-id
    useEffect(() => {
        const loadDeckFromHash = () => {
            const raw = window.location.hash.replace('#', '');
            if (!raw.startsWith('discover-decks/')) return;
            const segments = raw.split('/');
            // segments: ['discover-decks', 'author_name', 'deck-id']
            const deckId = segments.length >= 3 ? segments[segments.length - 1] : null;
            if (!deckId || !/^[0-9a-f-]{36}$/i.test(deckId)) return;

            deckService.getDeck(deckId).then(deck => {
                if (deck) {
                    setDiscoverMode('decks');
                    handleViewDeck(deck);
                }
            }).catch(err => {
                console.error('Failed to load shared deck:', err);
            });
        };

        loadDeckFromHash();

        // Also support legacy ?deck=UUID format
        const params = new URLSearchParams(window.location.search);
        const legacyDeckId = params.get('deck');
        if (legacyDeckId) {
            deckService.getDeck(legacyDeckId).then(deck => {
                if (deck) {
                    // Redirect to new format
                    const slug = (deck.author_name || 'anonymous').toLowerCase().replace(/\s+/g, '_');
                    window.history.replaceState(null, '', `#discover-decks/${slug}/${deck.id}`);
                    setDiscoverMode('decks');
                    handleViewDeck(deck);
                }
            }).catch(err => {
                console.error('Failed to load shared deck:', err);
            });
        }

        window.addEventListener('hashchange', loadDeckFromHash);
        return () => window.removeEventListener('hashchange', loadDeckFromHash);
    }, []);

    const loadDecks = async () => {
        setLoading(true);
        try {
            const publicDecks = await deckService.getPublicDecks(20);
            setDecks(publicDecks);
        } catch (err) {
            console.error('Failed to load decks:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadMyDecks = async () => {
        if (!user) return;
        setMyDecksLoading(true);
        try {
            const userDecks = await deckService.getUserDecks(user.id);
            setMyDecks(userDecks);
        } catch (err) {
            console.error('Failed to load my decks:', err);
        } finally {
            setMyDecksLoading(false);
        }
    };

    const handleCreateDeck = async () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        try {
            const authorName = user.user_metadata?.display_name
                || user.user_metadata?.name
                || user.email?.split('@')[0]
                || 'Anonymous';

            const newDeck = await deckService.saveDeck({
                name: newDeckTitle || 'Untitled Deck',
                format: newDeckFormat,
                colors: [],
                cards: {},
                lands: {},
                sideboard: {},
                commander: newDeckFormat === 'commander' && newDeckCommander ? { name: newDeckCommander, count: 1 } : null,
                companion: null,
                plans: null,
                author_name: authorName,
                author_id: user.id,
                user_id: user.id,
                deck_size: 0,
                land_count: 0,
                mana_curve: 0,
                is_public: newDeckVisibility === 'public',
            });

            // Reset form
            setShowAddDeck(false);
            setNewDeckTitle('Untitled Deck');
            setNewDeckFormat('standard');
            setNewDeckCommander('');
            setNewDeckVisibility('public');

            // Hot import into BREW tab
            if (onLoadDeck && newDeck) {
                onLoadDeck(newDeck.cards || {}, newDeck.lands || {}, newDeck.sideboard || {}, newDeck.name);
            }

            // Refresh deck lists
            loadDecks();
            if (discoverMode === 'mydecks') loadMyDecks();
        } catch (err) {
            console.error('Failed to create deck:', err);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            loadDecks();
            return;
        }
        setLoading(true);
        try {
            const results = await deckService.searchDecks(searchQuery);
            setDecks(results);
        } catch (err) {
            console.error('Search failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);

        try {
            if (authMode === 'signup') {
                await authService.signUpEmail(email, password, displayName);

                // Increment member count in stats
                const { deckService: { }, supabase: db } = await import('../lib/supabase');
                const { data: memberData } = await db
                    .from('stats')
                    .select('value')
                    .eq('key', 'member_count')
                    .single();

                await db.from('stats').upsert({
                    key: 'member_count',
                    value: (memberData?.value || 0) + 1,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'key' });

                setShowAuthModal(false);
            } else {
                await authService.signInEmail(email, password);
                setShowAuthModal(false);
            }
        } catch (err: any) {
            setAuthError(err.message || 'Authentication failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSignOut = async () => {
        await authService.signOut();
    };

    const handleSaveDeck = async (name: string, format: string, coverImage: string | null) => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        // Convert deck to storage format
        const cards: Record<string, number> = {};
        const lands: Record<string, number> = {};
        const sideboard: Record<string, number> = {};
        const colorSet = new Set<string>();

        // Collect plan configurations: { cardName: { base: qty, A: qty, B: qty, ... } }
        const plans: Record<string, Record<string, number>> = {};

        deck.forEach(card => {
            // Collect colors from all cards
            card.colors?.forEach(c => colorSet.add(c));
            card.colorIdentity?.forEach(c => colorSet.add(c));

            if (card.isSideboard) {
                sideboard[card.name] = (sideboard[card.name] || 0) + (card.quantity || 1);
            } else if (card.isLand || card.type?.includes('Land')) {
                lands[card.name] = (lands[card.name] || 0) + (card.quantity || 1);
            } else {
                cards[card.name] = (cards[card.name] || 0) + (card.quantity || 1);
            }

            // Save plan configurations if any exist
            if (card.planQuantities && Object.keys(card.planQuantities).length > 0) {
                if (!plans[card.name]) {
                    plans[card.name] = { base: card.quantity || 1 };
                }
                const pq = card.planQuantities;
                if (pq.A !== undefined) plans[card.name].A = pq.A;
                if (pq.B !== undefined) plans[card.name].B = pq.B;
                if (pq.C !== undefined) plans[card.name].C = pq.C;
                if (pq.D !== undefined) plans[card.name].D = pq.D;
                if (pq.E !== undefined) plans[card.name].E = pq.E;
            }
        });

        // Get author name with better fallbacks
        const authorName = user.user_metadata?.display_name
            || user.user_metadata?.name
            || user.user_metadata?.full_name
            || user.email?.split('@')[0]
            || 'Anonymous';

        // Get user's role for badge display
        const userRole = await roleService.getCurrentUserRole();

        // Check if user is a registered streamer
        const { data: streamerData } = await supabase
            .from('registered_streamers')
            .select('is_live')
            .eq('user_id', user.id)
            .single();

        const isStreamer = !!streamerData;
        const isLive = streamerData?.is_live || false;

        await deckService.saveDeck({
            name,
            format,
            colors: Array.from(colorSet),
            cards,
            lands,
            sideboard,
            plans: Object.keys(plans).length > 0 ? plans : null,
            cover_image: coverImage,
            author_name: authorName,
            author_id: user.id,
            author_role: userRole,
            author_is_streamer: isStreamer,
            author_is_live: isLive,
            user_id: user.id,
            deck_size: deckSize,
            land_count: landCount,
            mana_curve: manaCurve,
            is_public: true
        });

        // Award sparks for saving a deck
        const sparkResult = await sparksService.awardDeckSave('new');
        if (sparkResult.success && sparkResult.balance !== undefined) {
            setSparkBalance(sparkResult.balance);
            showSparkToast('Deck saved! +2 Sparks', 2, 'earn');
        }

        loadDecks();
    };

    // Check if user already voted on a deck (by checking DB-backed state)
    const hasVoted = (deckId: string, type?: 'up' | 'down'): boolean => {
        const vote = userVotes[deckId];
        if (type === 'up') return vote === 1;
        if (type === 'down') return vote === -1;
        return vote !== undefined;
    };

    // Load user votes when decks change
    useEffect(() => {
        if (user && decks.length > 0) {
            const deckIds = decks.map(d => d.id);
            deckService.getUserVotes(deckIds).then(setUserVotes);
        }
    }, [user, decks.length]);

    const handleVote = async (deckId: string, direction: 1 | -1) => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }
        try {
            const result = await deckService.voteDeck(deckId, direction);

            // Update local vote state
            setUserVotes(prev => {
                const next = { ...prev };
                if (result.userVote === null) {
                    delete next[deckId];
                } else {
                    next[deckId] = result.userVote;
                }
                return next;
            });

            // Update deck counts in local state
            setDecks(prev => prev.map(d =>
                d.id === deckId ? { ...d, upvotes: result.thumbsUp, downvotes: result.thumbsDown } : d
            ));
            if (selectedDeck?.id === deckId) {
                setSelectedDeck(prev => prev ? { ...prev, upvotes: result.thumbsUp, downvotes: result.thumbsDown } : null);
            }

            // Show spark milestone toast
            if (result.sparkAwarded) {
                showSparkToast('Your vote pushed the author to a spark milestone!', 0, 'earn');
            }

            // Refresh spark balance
            sparksService.getBalance().then(setSparkBalance);
        } catch (err: any) {
            if (err.message?.includes('Must be signed in')) {
                setShowAuthModal(true);
            } else {
                console.error('Vote failed:', err);
            }
        }
    };

    // Copy deck to clipboard as Arena format
    const handleCopyToClipboard = (deckRecord: DeckRecord) => {
        const lines: string[] = [];

        // Main deck cards
        Object.entries(deckRecord.cards || {}).forEach(([name, count]) => {
            lines.push(`${count} ${name}`);
        });

        // Lands
        Object.entries(deckRecord.lands || {}).forEach(([name, count]) => {
            lines.push(`${count} ${name}`);
        });

        // Sideboard
        if (Object.keys(deckRecord.sideboard || {}).length > 0) {
            lines.push('');
            lines.push('Sideboard');
            Object.entries(deckRecord.sideboard || {}).forEach(([name, count]) => {
                lines.push(`${count} ${name}`);
            });
        }

        navigator.clipboard.writeText(lines.join('\n'));
        alert('Deck copied to clipboard!');
    };

    const handleViewDeck = async (deckRecord: DeckRecord) => {
        setSelectedDeck(deckRecord);
        setLoadingComments(true);

        // Track view - only count once per session per deck
        const viewedDecks = JSON.parse(sessionStorage.getItem('viewed-decks') || '[]');
        if (!viewedDecks.includes(deckRecord.id)) {
            try {
                await deckService.incrementViews(deckRecord.id);
                // Update local state
                setDecks(prev => prev.map(d =>
                    d.id === deckRecord.id ? { ...d, views: (d.views || 0) + 1 } : d
                ));
                setSelectedDeck(prev => prev ? { ...prev, views: (prev.views || 0) + 1 } : null);
                // Mark as viewed this session
                viewedDecks.push(deckRecord.id);
                sessionStorage.setItem('viewed-decks', JSON.stringify(viewedDecks));
            } catch (err) {
                console.error('Failed to track view:', err);
            }
        }

        try {
            const deckComments = await commentService.getComments(deckRecord.id);
            setComments(deckComments);
        } catch (err) {
            console.error('Failed to load comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim() || !selectedDeck || !user) return;

        try {
            const comment = await commentService.addComment(
                selectedDeck.id,
                user.user_metadata?.display_name || user.email?.split('@')[0] || 'Anonymous',
                newComment,
                user.id
            );
            setComments(prev => [comment, ...prev]);
            setNewComment('');
        } catch (err) {
            console.error('Failed to add comment:', err);
        }
    };

    const handleLoadDeck = (deckRecord: DeckRecord) => {
        if (onLoadDeck) {
            onLoadDeck(deckRecord.cards, deckRecord.lands, deckRecord.sideboard, deckRecord.name);
        }
        setSelectedDeck(null);
    };

    // Handle MetaPanel → Brew tab navigation with deck data
    const handleExamineInBrew = (archetype: Archetype) => {
        // Convert key cards to deck format (1x each as a starting brew scaffold)
        const cards: Record<string, number> = {};
        for (const cardName of archetype.keyCards) {
            cards[cardName] = 1;
        }
        // Load into Brew tab
        if (onLoadDeck) {
            onLoadDeck(cards, {}, {}, `${archetype.name} (Meta)`);
        }
        // Navigate to Brew tab
        window.location.hash = '#brew';
    };

    // Launch playtester with enriched card data (images, cmc, etc.)
    const launchPlaytester = async (deckRecord: DeckRecord, mode: 'goldfish' | 'session' = 'goldfish') => {
        setPlaytesterLoading(true);
        setPlaytesterDeck(deckRecord);
        setPlaytesterMode(mode);

        try {
            const enrichedCards: typeof playtesterCards = [];

            // Fetch all cards (non-lands)
            for (const [name, quantity] of Object.entries(deckRecord.cards || {})) {
                try {
                    const cardData = await fetchCardData(name, 'exact');
                    enrichedCards.push({
                        name,
                        quantity,
                        imageUrl: getCardImageUrl(cardData),
                        cmc: cardData.cmc || 0,
                        isLand: cardData.type_line?.toLowerCase().includes('land') || false,
                        colors: cardData.colors || [],
                    });
                } catch {
                    // Fallback without image
                    enrichedCards.push({
                        name,
                        quantity,
                        cmc: 0,
                        isLand: false,
                        colors: deckRecord.colors || [],
                    });
                }
            }

            // Fetch lands
            for (const [name, quantity] of Object.entries(deckRecord.lands || {})) {
                try {
                    const cardData = await fetchCardData(name, 'exact');
                    enrichedCards.push({
                        name,
                        quantity,
                        imageUrl: getCardImageUrl(cardData),
                        cmc: 0,
                        isLand: true,
                        colors: [],
                    });
                } catch {
                    enrichedCards.push({
                        name,
                        quantity,
                        cmc: 0,
                        isLand: true,
                        colors: [],
                    });
                }
            }

            // For session mode, navigate to SESSION tab with deck data
            if (mode === 'session' && onNavigateToSession) {
                onNavigateToSession({
                    cards: enrichedCards,
                    deckName: deckRecord.name,
                });
            } else {
                // Goldfish mode - open full-screen playtester
                setPlaytesterCards(enrichedCards);
                setShowPlaytester(true);
            }
        } catch (err) {
            console.error('Failed to load playtester cards:', err);
        } finally {
            setPlaytesterLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Section Header: Search + Dropdown + Refresh + Viewers */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 sm:p-4 border-b border-white/10">
                {/* Search bar (shown for decks/mydecks modes) */}
                {(discoverMode === 'decks' || discoverMode === 'mydecks') && (
                    <>
                        {/* [+] Add Deck */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    if (!user) { setShowAuthModal(true); return; }
                                    setShowAddDeck(!showAddDeck);
                                }}
                                className={`flex items-center justify-center size-8 rounded-lg text-sm font-bold transition-all ${showAddDeck
                                    ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40 rotate-45'
                                    : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                                    }`}
                                title="Add new deck"
                            >
                                <Plus className="size-4" />
                            </button>
                        </div>
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/40" />
                            <input
                                type="text"
                                placeholder="Search decks..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-purple-500/50"
                            />
                        </div>
                        <button
                            onClick={handleSearch}
                            className="px-2 sm:px-3 py-1.5 bg-purple-500/20 text-amber-400 rounded-lg text-xs hover:bg-purple-500/30 transition-colors"
                        >
                            Search
                        </button>
                    </>
                )}

                {/* Spacer — pushes dropdown+refresh to the right */}
                <div className="flex-grow" />

                {/* Section Tabs Dropdown */}
                <SubNavDropdown
                    items={[
                        ...(user ? [{
                            key: 'mydecks',
                            label: 'My Decks',
                            icon: FolderOpen,
                            color: 'text-cyan-400',
                            count: myDecks.length,
                        }] : []),
                        {
                            key: 'decks',
                            label: 'Discover Decks',
                            icon: BookOpen,
                            color: 'text-amber-400',
                            count: decks.length,
                        },
                        {
                            key: 'streamers',
                            label: 'Discover Streamers',
                            icon: Tv,
                            color: 'text-amber-400',
                            count: streamers.length > 0 ? streamers.length : undefined,
                            countColor: streamers.length > 0 ? 'bg-red-500/30 text-red-400' : undefined,
                        },
                        {
                            key: 'meta',
                            label: 'Discover Meta',
                            icon: Sword,
                            color: 'text-red-400',
                        },
                    ]}
                    activeKey={discoverMode}
                    onSelect={(key: string) => {
                        setDiscoverMode(key as typeof discoverMode);
                        if (key === 'mydecks') loadMyDecks();
                        if (key === 'streamers') loadStreamers(true);
                    }}
                />

                {/* Refresh button — consistent purple */}
                <button
                    onClick={() => {
                        if (discoverMode === 'decks') { setSearchQuery(''); loadDecks(); }
                        else if (discoverMode === 'mydecks') loadMyDecks();
                        else if (discoverMode === 'streamers') loadStreamers(true);
                    }}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-medium transition-colors border border-purple-500/20"
                    title="Refresh"
                >
                    <RefreshCw className="size-3.5" />
                    <span className="hidden sm:inline">Refresh</span>
                </button>

                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1 sm:gap-1.5 text-white/40 text-xs">
                        <Users className="size-3.5 text-green-400" />
                        <span className="text-green-400 font-medium">{liveViewers}</span>
                        <span className="hidden sm:inline">viewing</span>
                    </div>

                    {user && (
                        <div className="flex items-center gap-2">
                            {/* Spark balance pill */}
                            <div className="flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
                                <img src="/spark-icon.png" alt="Sparks" className="w-4 h-4" />
                                <span className="text-xs font-bold text-amber-400">{sparkBalance}</span>
                            </div>

                            {/* Daily claim button */}
                            {canClaimDaily && (
                                <button
                                    onClick={async () => {
                                        setClaimingDaily(true);
                                        const result = await sparksService.claimDailySignIn();
                                        if (result.success) {
                                            showSparkToast(result.message, 1, 'earn');
                                            if (result.balance !== undefined) setSparkBalance(result.balance);
                                            setCanClaimDaily(false);
                                        }
                                        setClaimingDaily(false);
                                    }}
                                    disabled={claimingDaily}
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-amber-600/30 to-orange-600/30 border border-amber-500/30 text-amber-300 rounded-full text-[10px] font-medium hover:from-amber-600/50 hover:to-orange-600/50 transition-all animate-pulse hover:animate-none"
                                >
                                    <Gift className="size-3" />
                                    {claimingDaily ? '...' : 'Daily!'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4">
                {selectedDeck ? (
                    // Deck Detail View
                    <div className="max-w-3xl mx-auto">
                        <button
                            onClick={() => setSelectedDeck(null)}
                            className="text-amber-400 text-sm mb-4 hover:underline"
                        >
                            ← Back to decks
                        </button>

                        <div
                            className="bg-white/5 border border-white/10 rounded-lg p-6 relative overflow-hidden"
                            style={selectedDeck.cover_image ? {
                                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.8), rgba(0,0,0,0.95)), url(${selectedDeck.cover_image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            } : {}}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    {editMode ? (
                                        <div className="space-y-2">
                                            <input
                                                type="text"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                className="text-2xl font-bold text-white bg-white/10 border border-white/20 rounded px-2 py-1 w-full"
                                            />
                                            <select
                                                value={editFormat}
                                                onChange={(e) => setEditFormat(e.target.value)}
                                                className="text-sm text-white bg-white/10 border border-white/20 rounded px-2 py-1"
                                            >
                                                <option value="standard">Standard</option>
                                                <option value="modern">Modern</option>
                                                <option value="pioneer">Pioneer</option>
                                                <option value="legacy">Legacy</option>
                                                <option value="vintage">Vintage</option>
                                                <option value="commander">Commander</option>
                                                <option value="pauper">Pauper</option>
                                                <option value="limited">Limited</option>
                                                <option value="historic">Historic</option>
                                                <option value="explorer">Explorer</option>
                                            </select>
                                        </div>
                                    ) : (
                                        <>
                                            <h2 className="text-2xl font-bold text-white">{selectedDeck.name}</h2>
                                            <p className="text-white/60">
                                                by {selectedDeck.author_name} • {selectedDeck.format} • {selectedDeck.deck_size} cards
                                            </p>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {editMode ? (
                                        <>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const updated = await deckService.updateDeck(selectedDeck.id, {
                                                            name: editName,
                                                            format: editFormat
                                                        });
                                                        setSelectedDeck(updated);
                                                        setEditMode(false);
                                                        loadDecks();
                                                    } catch (err) {
                                                        console.error('Failed to update:', err);
                                                        alert('Failed to update deck');
                                                    }
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                            >
                                                <Check className="size-4" />
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditMode(false)}
                                                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                                            >
                                                <X className="size-4" />
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Goldfish = private solo testing */}
                                            <button
                                                onClick={() => launchPlaytester(selectedDeck, 'goldfish')}
                                                disabled={playtesterLoading}
                                                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-500 hover:to-emerald-500 transition-colors shadow-lg disabled:opacity-50 text-sm"
                                                title="Solo playtesting - nothing is broadcast"
                                            >
                                                {playtesterLoading ? (
                                                    <Loader2 className="size-4 animate-spin" />
                                                ) : (
                                                    <Play className="size-4" />
                                                )}
                                                Goldfish
                                            </button>
                                            {/* Stream Mode = session mode with OBS broadcast */}
                                            <button
                                                onClick={() => launchPlaytester(selectedDeck, 'session')}
                                                disabled={playtesterLoading}
                                                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-500 hover:to-pink-500 transition-colors shadow-lg disabled:opacity-50 text-sm"
                                                title="Stream-safe mode - hand hidden from OBS overlay"
                                            >
                                                {playtesterLoading ? (
                                                    <Loader2 className="size-4 animate-spin" />
                                                ) : (
                                                    <Radio className="size-4" />
                                                )}
                                                Stream
                                            </button>
                                            <button
                                                onClick={() => handleLoadDeck(selectedDeck)}
                                                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                                            >
                                                <Download className="size-4" />
                                                Load Deck
                                            </button>
                                            {/* Edit - owner only */}
                                            {user && selectedDeck.user_id === user.id && (
                                                <button
                                                    onClick={() => {
                                                        setEditName(selectedDeck.name);
                                                        setEditFormat(selectedDeck.format);
                                                        setEditMode(true);
                                                    }}
                                                    className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                                                >
                                                    <Pencil className="size-4" />
                                                    Edit
                                                </button>
                                            )}

                                            {/* Delete - owner OR mod/admin */}
                                            {user && (selectedDeck.user_id === user.id || userRole === 'mod' || userRole === 'admin') && (
                                                <button
                                                    onClick={async () => {
                                                        const isOwner = selectedDeck.user_id === user.id;
                                                        const confirmMsg = isOwner
                                                            ? 'Are you sure you want to delete this deck?'
                                                            : `Delete deck "${selectedDeck.name}" by ${selectedDeck.author_name}? (Mod action)`;
                                                        if (confirm(confirmMsg)) {
                                                            try {
                                                                await deckService.deleteDeck(selectedDeck.id);
                                                                setSelectedDeck(null);
                                                                loadDecks();
                                                            } catch (err) {
                                                                console.error('Failed to delete:', err);
                                                                alert('Failed to delete deck');
                                                            }
                                                        }
                                                    }}
                                                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                                >
                                                    <Trash2 className="size-4" />
                                                    Delete
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Voting + Copy */}
                            <div className="flex items-center gap-4 mb-6">
                                <button
                                    onClick={() => handleVote(selectedDeck.id, 1)}
                                    className={`flex items-center gap-1 transition-colors ${hasVoted(selectedDeck.id, 'up')
                                        ? 'text-green-400'
                                        : 'text-white/60 hover:text-green-400'
                                        }`}
                                    title={hasVoted(selectedDeck.id, 'up') ? 'Click to remove upvote' : 'Upvote this deck'}
                                >
                                    <ThumbsUp className="size-5" />
                                    <span>{selectedDeck.upvotes || 0}</span>
                                </button>
                                <button
                                    onClick={() => handleVote(selectedDeck.id, -1)}
                                    className={`flex items-center gap-1 transition-colors ${hasVoted(selectedDeck.id, 'down')
                                        ? 'text-red-400'
                                        : 'text-white/60 hover:text-red-400'
                                        }`}
                                    title={hasVoted(selectedDeck.id, 'down') ? 'Click to remove downvote' : 'Downvote this deck'}
                                >
                                    <ThumbsDown className="size-5" />
                                    <span>{selectedDeck.downvotes || 0}</span>
                                </button>
                                <span className="flex items-center gap-1 text-white/40">
                                    <Eye className="size-4" /> {selectedDeck.views}
                                </span>
                                <button
                                    onClick={() => handleCopyToClipboard(selectedDeck)}
                                    className="flex items-center gap-1 text-amber-400 hover:text-amber-300"
                                >
                                    <Copy className="size-4" /> Copy to Clipboard
                                </button>
                                <button
                                    onClick={() => {
                                        const slug = (selectedDeck.author_name || 'anonymous').toLowerCase().replace(/\s+/g, '_');
                                        const shareUrl = `${window.location.origin}${window.location.pathname}#discover-decks/${slug}/${selectedDeck.id}`;
                                        navigator.clipboard.writeText(shareUrl);
                                        // Update URL to match
                                        window.history.replaceState(null, '', `#discover-decks/${slug}/${selectedDeck.id}`);
                                        alert('Share link copied to clipboard!');
                                    }}
                                    className="flex items-center gap-1 text-purple-400 hover:text-purple-300"
                                >
                                    <Share2 className="size-4" /> Share Link
                                </button>
                            </div>

                            {/* Full Deck List */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-white mb-2">Spells ({Object.values(selectedDeck.cards || {}).reduce((a, b) => a + b, 0)})</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-white/80 max-h-60 overflow-y-auto">
                                    {Object.entries(selectedDeck.cards || {}).map(([name, count]) => (
                                        <div key={name} className="bg-white/5 px-2 py-1 rounded">
                                            {count}x {name}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Lands */}
                            {Object.keys(selectedDeck.lands || {}).length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-white mb-2">Lands ({Object.values(selectedDeck.lands || {}).reduce((a, b) => a + b, 0)})</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-white/80">
                                        {Object.entries(selectedDeck.lands || {}).map(([name, count]) => (
                                            <div key={name} className="bg-white/5 px-2 py-1 rounded">
                                                {count}x {name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sideboard */}
                            {Object.keys(selectedDeck.sideboard || {}).length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-white mb-2">Sideboard ({Object.values(selectedDeck.sideboard || {}).reduce((a, b) => a + b, 0)})</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-white/80">
                                        {Object.entries(selectedDeck.sideboard || {}).map(([name, count]) => (
                                            <div key={name} className="bg-white/5 px-2 py-1 rounded">
                                                {count}x {name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Comments */}
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                    <MessageCircle className="size-5" />
                                    Comments ({comments.length})
                                </h3>

                                {user && (
                                    <div className="flex gap-2 mb-4">
                                        <input
                                            type="text"
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            placeholder="Add a comment..."
                                            className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                        />
                                        <button
                                            onClick={handleAddComment}
                                            disabled={!newComment.trim()}
                                            className="px-4 py-2 bg-purple-500/20 text-amber-400 rounded-lg text-sm hover:bg-purple-500/30 disabled:opacity-50"
                                        >
                                            Post
                                        </button>
                                    </div>
                                )}

                                {loadingComments ? (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="size-6 animate-spin text-amber-400" />
                                    </div>
                                ) : comments.length === 0 ? (
                                    <p className="text-white/40 text-sm text-center py-4">
                                        No comments yet. Be the first!
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {comments.map(comment => (
                                            <div key={comment.id} className="bg-white/5 rounded-lg p-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="font-medium text-white/80">{comment.author_name}</span>
                                                    <span className="text-xs text-white/40">
                                                        {new Date(comment.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="text-white/60 text-sm">{comment.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : discoverMode === 'decks' ? (
                    // Decks Section
                    <>
                        {/* Add Deck Popover (anchored to header + button) */}
                        {showAddDeck && (
                            <div className="mx-4 mb-3 w-80 bg-slate-900 border border-white/15 rounded-xl p-4 shadow-2xl z-50 animate-in fade-in slide-in-from-top-2">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-white">New Deck</h3>
                                    <button onClick={() => setShowAddDeck(false)} className="text-white/40 hover:text-white/60">
                                        <X className="size-4" />
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {/* Title */}
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Deck Name</label>
                                        <input
                                            type="text"
                                            value={newDeckTitle}
                                            onChange={(e) => setNewDeckTitle(e.target.value)}
                                            onFocus={(e) => e.target.value === 'Untitled Deck' && setNewDeckTitle('')}
                                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                            placeholder="My Awesome Deck"
                                        />
                                    </div>

                                    {/* Format */}
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Format</label>
                                        <select
                                            value={newDeckFormat}
                                            onChange={(e) => setNewDeckFormat(e.target.value)}
                                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                        >
                                            <option value="standard">Standard</option>
                                            <option value="modern">Modern</option>
                                            <option value="pioneer">Pioneer</option>
                                            <option value="legacy">Legacy</option>
                                            <option value="vintage">Vintage</option>
                                            <option value="commander">Commander</option>
                                            <option value="pauper">Pauper</option>
                                            <option value="limited">Limited</option>
                                            <option value="historic">Historic</option>
                                            <option value="explorer">Explorer</option>
                                        </select>
                                    </div>

                                    {/* Commander (conditional) */}
                                    {newDeckFormat === 'commander' && (
                                        <div>
                                            <label className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                                <Crown className="size-3 text-amber-400" />
                                                Commander <span className="text-white/30">(optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newDeckCommander}
                                                onChange={(e) => setNewDeckCommander(e.target.value)}
                                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50"
                                                placeholder="e.g. Atraxa, Praetors' Voice"
                                            />
                                        </div>
                                    )}

                                    {/* Visibility */}
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Visibility</label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setNewDeckVisibility('public')}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${newDeckVisibility === 'public'
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                                                    }`}
                                            >
                                                <Globe className="size-3" /> Public
                                            </button>
                                            <button
                                                onClick={() => setNewDeckVisibility('private')}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${newDeckVisibility === 'private'
                                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                                                    }`}
                                            >
                                                <Lock className="size-3" /> Private
                                            </button>
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={handleCreateDeck}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/30 to-amber-500/30 text-amber-400 rounded-lg text-sm font-medium hover:from-purple-500/40 hover:to-amber-500/40 transition-all border border-purple-500/20"
                                        >
                                            <Play className="size-3.5" />
                                            Create & Open in BREW
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* User Profile Overlay */}
                        {viewingUserProfile ? (
                            <div className="flex flex-col h-full">
                                {/* Profile Header */}
                                <div className="flex items-center gap-3 mb-4 p-3 bg-white/5 border border-white/10 rounded-lg">
                                    <button
                                        onClick={() => { setViewingUserProfile(null); setProfileDecks([]); }}
                                        className="flex items-center gap-1.5 px-2 py-1 bg-white/5 text-white/60 rounded-lg text-xs hover:bg-white/10 transition-colors"
                                    >
                                        <ArrowLeft className="size-3.5" /> Back
                                    </button>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-white">{viewingUserProfile.name}'s Decks</h3>
                                        <p className="text-xs text-white/40">{profileDecks.length} public deck{profileDecks.length !== 1 ? 's' : ''}</p>
                                    </div>
                                    {user && viewingUserProfile.id !== user.id && (
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs font-medium hover:bg-cyan-500/30 transition-colors border border-cyan-500/30"
                                                title="Add friend"
                                            >
                                                <UserPlus className="size-3.5" /> Add Friend
                                            </button>
                                            <button
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-colors border border-purple-500/30"
                                                title="Send message"
                                            >
                                                <Mail className="size-3.5" /> Message
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Profile Decks (grouped by format) */}
                                {profileDecksLoading ? (
                                    <div className="flex items-center justify-center h-64">
                                        <Loader2 className="size-8 animate-spin text-cyan-400" />
                                    </div>
                                ) : profileDecks.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-64 text-white/40">
                                        <FolderOpen className="size-12 mb-4 opacity-20" />
                                        <p className="text-sm">No public decks shared yet.</p>
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-y-auto space-y-4">
                                        {groupDecksByFormat(profileDecks).map(([format, formatDecks]) => (
                                            <div key={format}>
                                                <h4 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <BookOpen className="size-3.5" />
                                                    {format} <span className="text-white/30 font-normal">({formatDecks.length})</span>
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    {formatDecks.map(deckRecord => (
                                                        <div
                                                            key={deckRecord.id}
                                                            onClick={() => handleViewDeck(deckRecord)}
                                                            className="bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-purple-500/30 transition-colors cursor-pointer group"
                                                            style={deckRecord.cover_image ? {
                                                                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${deckRecord.cover_image})`,
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center top'
                                                            } : {}}
                                                        >
                                                            <div className="p-4">
                                                                <h3 className="font-medium text-white truncate group-hover:text-amber-400 transition-colors mb-1">
                                                                    {deckRecord.name}
                                                                </h3>
                                                                <p className="text-xs text-white/50 mb-3">
                                                                    {deckRecord.deck_size} cards
                                                                    {deckRecord.commander && (
                                                                        <span className="text-amber-400/60"> • <Crown className="size-3 inline" /> {typeof deckRecord.commander === 'object' ? deckRecord.commander.name : deckRecord.commander}</span>
                                                                    )}
                                                                </p>
                                                                <div className="flex items-center gap-3 text-xs text-white/40">
                                                                    <span className="flex items-center gap-1 text-green-400/60">
                                                                        <ThumbsUp className="size-3" /> {deckRecord.upvotes || 0}
                                                                    </span>
                                                                    <span className="flex items-center gap-1">
                                                                        <Eye className="size-3" /> {deckRecord.views}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : loading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="size-8 animate-spin text-amber-400" />
                            </div>
                        ) : decks.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-white/40">
                                <Compass className="size-12 mb-4 opacity-20" />
                                <h3 className="text-lg font-medium text-white/60 mb-2">No Decks Found</h3>
                                <p className="text-sm text-center max-w-md">
                                    Be the first to share a deck! Build your deck in the BREW tab, then save it here.
                                </p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto space-y-5">
                                {groupDecksByFormat(decks).map(([format, formatDecks]) => (
                                    <div key={format}>
                                        <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3 flex items-center gap-2 border-b border-white/5 pb-2">
                                            <BookOpen className="size-3.5" />
                                            {format} <span className="text-white/30 font-normal">({formatDecks.length})</span>
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {formatDecks.map(deckRecord => (
                                                <div
                                                    key={deckRecord.id}
                                                    onClick={() => handleViewDeck(deckRecord)}
                                                    className="bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-purple-500/30 transition-colors cursor-pointer group"
                                                    style={deckRecord.cover_image ? {
                                                        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${deckRecord.cover_image})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center top'
                                                    } : {}}
                                                >
                                                    <div className="p-4">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <h3 className="font-medium text-white truncate flex-1 group-hover:text-amber-400 transition-colors">
                                                                {deckRecord.name}
                                                            </h3>
                                                        </div>
                                                        <p className="text-xs text-white/50 mb-3 flex items-center gap-1.5">
                                                            by{' '}
                                                            {deckRecord.author_id ? (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); viewUserProfile(deckRecord.author_id!, deckRecord.author_name); }}
                                                                    className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                                                                >
                                                                    {deckRecord.author_name}
                                                                </button>
                                                            ) : (
                                                                <span>{deckRecord.author_name}</span>
                                                            )}
                                                            {/* Mod badge */}
                                                            {deckRecord.author_role === 'mod' && (
                                                                <span className="text-green-400" title="Moderator">
                                                                    <Sword className="size-3" />
                                                                </span>
                                                            )}
                                                            {/* Admin badge */}
                                                            {deckRecord.author_role === 'admin' && (
                                                                <span className="text-red-400" title="Admin">
                                                                    <Sword className="size-3" />
                                                                </span>
                                                            )}
                                                            {/* Streamer badge */}
                                                            {deckRecord.author_is_streamer && (
                                                                <span className="inline-flex items-center gap-0.5" title={deckRecord.author_is_live ? 'Live now!' : 'Registered streamer'}>
                                                                    <Tv className="size-3 text-purple-400" />
                                                                    <span className={`w-1.5 h-1.5 rounded-full ${deckRecord.author_is_live ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                                                                </span>
                                                            )}
                                                            <span>• {deckRecord.deck_size} cards</span>
                                                        </p>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3 text-xs text-white/40">
                                                                <span className="flex items-center gap-1 text-green-400/60">
                                                                    <ThumbsUp className="size-3" /> {deckRecord.upvotes || 0}
                                                                </span>
                                                                <span className="flex items-center gap-1 text-red-400/60">
                                                                    <ThumbsDown className="size-3" /> {deckRecord.downvotes || 0}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Eye className="size-3" /> {deckRecord.views}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : discoverMode === 'mydecks' ? (
                    // My Decks Section
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <FolderOpen className="size-5 text-cyan-400" />
                                <h3 className="text-lg font-medium text-white">My Decks</h3>
                                <span className="text-white/40 text-sm">({myDecks.length})</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* [+] Add Deck Button in My Decks too */}
                                <button
                                    onClick={() => setShowAddDeck(!showAddDeck)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors border border-green-500/30"
                                >
                                    <Plus className="size-3.5" /> New Deck
                                </button>
                            </div>
                        </div>

                        {/* Add Deck Popover (shared state from decks mode) */}
                        {showAddDeck && (
                            <div className="mb-4 bg-slate-900 border border-white/15 rounded-xl p-4 shadow-2xl">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-white">New Deck</h3>
                                    <button onClick={() => setShowAddDeck(false)} className="text-white/40 hover:text-white/60">
                                        <X className="size-4" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Deck Name</label>
                                        <input
                                            type="text"
                                            value={newDeckTitle}
                                            onChange={(e) => setNewDeckTitle(e.target.value)}
                                            onFocus={(e) => e.target.value === 'Untitled Deck' && setNewDeckTitle('')}
                                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                            placeholder="My Awesome Deck"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Format</label>
                                        <select
                                            value={newDeckFormat}
                                            onChange={(e) => setNewDeckFormat(e.target.value)}
                                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                        >
                                            <option value="standard">Standard</option>
                                            <option value="modern">Modern</option>
                                            <option value="pioneer">Pioneer</option>
                                            <option value="legacy">Legacy</option>
                                            <option value="vintage">Vintage</option>
                                            <option value="commander">Commander</option>
                                            <option value="pauper">Pauper</option>
                                            <option value="limited">Limited</option>
                                            <option value="historic">Historic</option>
                                            <option value="explorer">Explorer</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-white/50 mb-1 block">Visibility</label>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => setNewDeckVisibility('public')}
                                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${newDeckVisibility === 'public'
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                                                    }`}
                                            >
                                                <Globe className="size-3" /> Public
                                            </button>
                                            <button
                                                onClick={() => setNewDeckVisibility('private')}
                                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${newDeckVisibility === 'private'
                                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                                                    }`}
                                            >
                                                <Lock className="size-3" /> Private
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCreateDeck}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/30 to-amber-500/30 text-amber-400 rounded-lg text-sm font-medium hover:from-purple-500/40 hover:to-amber-500/40 transition-all border border-purple-500/20"
                                    >
                                        <Play className="size-3.5" />
                                        Create & Open in BREW
                                    </button>
                                </div>
                                {newDeckFormat === 'commander' && (
                                    <div className="mt-3 max-w-sm">
                                        <label className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                            <Crown className="size-3 text-amber-400" />
                                            Commander <span className="text-white/30">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newDeckCommander}
                                            onChange={(e) => setNewDeckCommander(e.target.value)}
                                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50"
                                            placeholder="e.g. Atraxa, Praetors' Voice"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Deck List */}
                        <div className="flex-1 overflow-y-auto">
                            {myDecksLoading ? (
                                <div className="flex items-center justify-center h-64">
                                    <Loader2 className="size-8 animate-spin text-cyan-400" />
                                </div>
                            ) : myDecks.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-64 text-white/40">
                                    <FolderOpen className="size-12 mb-4 opacity-20" />
                                    <h3 className="text-lg font-medium text-white/60 mb-2">No Decks Yet</h3>
                                    <p className="text-sm text-center max-w-md mb-4">
                                        Create your first deck using the <span className="text-green-400">+ New Deck</span> button above,
                                        or build one in the BREW tab and save it here.
                                    </p>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto space-y-4">
                                    {groupDecksByFormat(myDecks).map(([format, formatDecks]) => (
                                        <div key={format}>
                                            <h4 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-2 border-b border-white/5 pb-2">
                                                <BookOpen className="size-3.5" />
                                                {format} <span className="text-white/30 font-normal">({formatDecks.length})</span>
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {formatDecks.map(myDeck => (
                                                    <div
                                                        key={myDeck.id}
                                                        className="bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-cyan-500/30 transition-colors group"
                                                        style={myDeck.cover_image ? {
                                                            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${myDeck.cover_image})`,
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center top'
                                                        } : {}}
                                                    >
                                                        <div className="p-4">
                                                            <div className="flex items-start justify-between mb-2">
                                                                <h3 className="font-medium text-white truncate flex-1 group-hover:text-cyan-400 transition-colors">
                                                                    {myDeck.name}
                                                                </h3>
                                                                <div className="flex items-center gap-1.5 ml-2">
                                                                    {myDeck.is_public ? (
                                                                        <span title="Public"><Globe className="size-3 text-green-400/60" /></span>
                                                                    ) : (
                                                                        <span title="Private"><Lock className="size-3 text-amber-400/60" /></span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-white/50 mb-3">
                                                                {myDeck.deck_size || 0} cards • {myDeck.land_count || 0} lands
                                                                {myDeck.commander && (
                                                                    <span className="text-amber-400/60"> • <Crown className="size-3 inline" /> {typeof myDeck.commander === 'object' ? myDeck.commander.name : myDeck.commander}</span>
                                                                )}
                                                            </p>
                                                            <div className="flex items-center gap-3 text-xs text-white/40 mb-3">
                                                                <span className="flex items-center gap-1 text-green-400/60">
                                                                    <ThumbsUp className="size-3" /> {myDeck.upvotes || 0}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Eye className="size-3" /> {myDeck.views || 0}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Copy className="size-3" /> {myDeck.copies || 0}
                                                                </span>
                                                            </div>
                                                            {/* Actions */}
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleLoadDeck(myDeck); }}
                                                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-colors"
                                                                    title="Load into BREW tab"
                                                                >
                                                                    <Play className="size-3" /> Open in BREW
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleViewDeck(myDeck); }}
                                                                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white/5 text-white/60 rounded-lg text-xs hover:bg-white/10 transition-colors"
                                                                    title="View deck details"
                                                                >
                                                                    <Eye className="size-3" /> View
                                                                </button>
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (!confirm('Delete this deck?')) return;
                                                                        try {
                                                                            await deckService.deleteDeck(myDeck.id);
                                                                            setMyDecks(prev => prev.filter(d => d.id !== myDeck.id));
                                                                        } catch (err) {
                                                                            console.error('Failed to delete:', err);
                                                                        }
                                                                    }}
                                                                    className="flex items-center justify-center p-1.5 text-red-400/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                                                    title="Delete deck"
                                                                >
                                                                    <Trash2 className="size-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : discoverMode === 'streamers' ? (
                    // Streamers Section - Unified list (live first)
                    <div className="flex flex-col h-full">
                        {/* Header with status and refresh */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span className="text-white/60 text-sm">
                                    {streamers.length} live • {offlineStreamers.length} offline
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Manual status toggle for registered streamers */}
                                {isRegisteredStreamer && (
                                    <button
                                        onClick={toggleMyLiveStatus}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${myLiveStatus
                                            ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                                            : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                                            }`}
                                    >
                                        {myLiveStatus ? '🔴 Go Offline' : '🟢 Go Live'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Unified Streamer Grid - Live first, then Offline */}
                        <div className="flex-1 overflow-y-auto">
                            {streamers.length === 0 && offlineStreamers.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-64 text-white/40">
                                    <Tv className="size-12 mb-4 opacity-20" />
                                    <h3 className="text-lg font-medium text-white/60 mb-2">No Registered Streamers</h3>
                                    <p className="text-sm text-center max-w-md">
                                        Be the first to register! Click "Streamer Tools" below.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {/* Live streamers first */}
                                    {streamers.map(streamer => (
                                        <div
                                            key={streamer.id}
                                            onClick={() => setWatchingStreamer(streamer)}
                                            className="relative aspect-video bg-black/50 rounded-lg overflow-hidden cursor-pointer group border border-green-500/30 hover:border-green-500/50 transition-all"
                                        >
                                            {streamer.thumbnailUrl ? (
                                                <img
                                                    src={streamer.thumbnailUrl}
                                                    alt={`${streamer.displayName} stream`}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                                                    <Tv className="size-8 text-green-400/50" />
                                                </div>
                                            )}
                                            {/* LIVE badge - always visible */}
                                            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded text-xs font-bold">
                                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                LIVE
                                            </div>
                                            {/* Viewer count - always visible */}
                                            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-black/70 text-white rounded text-xs">
                                                <Users className="size-3" />
                                                {streamer.viewerCount}
                                            </div>
                                            {/* Hover overlay with name and info */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {streamer.profileImageUrl && (
                                                        <img
                                                            src={streamer.profileImageUrl}
                                                            alt={streamer.displayName}
                                                            className="w-6 h-6 rounded-full object-cover border border-green-500/50"
                                                        />
                                                    )}
                                                    <span className="font-medium text-sm text-white truncate">{streamer.displayName}</span>
                                                </div>
                                                {streamer.streamTitle && (
                                                    <p className="text-xs text-white/80 line-clamp-1 mb-1">{streamer.streamTitle}</p>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    {streamer.gameName && (
                                                        <span className="text-green-400 text-xs truncate">🎮 {streamer.gameName}</span>
                                                    )}
                                                    <span className="text-green-400 text-xs ml-auto">Watch →</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {/* Offline streamers after */}
                                    {offlineStreamers.map(streamer => (
                                        <a
                                            key={streamer.id}
                                            href={streamer.twitchUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="relative aspect-video bg-gradient-to-br from-gray-700/30 to-gray-900/30 rounded-lg overflow-hidden group border border-white/10 hover:border-purple-500/30 transition-all"
                                        >
                                            {/* Profile image in center */}
                                            {streamer.profileImageUrl ? (
                                                <img
                                                    src={streamer.profileImageUrl}
                                                    alt={streamer.displayName}
                                                    className="absolute inset-0 m-auto w-12 h-12 rounded-full object-cover border-2 border-white/10 opacity-50"
                                                />
                                            ) : (
                                                <Tv className="absolute inset-0 m-auto size-8 text-white/20" />
                                            )}
                                            {/* OFFLINE badge - always visible */}
                                            <div className="absolute top-2 left-2 px-2 py-0.5 bg-gray-600/80 text-white/70 rounded text-xs">
                                                OFFLINE
                                            </div>
                                            {/* Hover overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                                <span className="font-medium text-sm text-white truncate">{streamer.displayName}</span>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-white/50">@{streamer.username}</span>
                                                    <span className="text-purple-400 text-xs">Visit →</span>
                                                </div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Bottom Actions - inside the flex container */}
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-center gap-4 flex-shrink-0">
                            <button
                                onClick={() => setShowBroadcastModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg"
                            >
                                <Radio className="size-4" />
                                Streamer Setup
                            </button>
                            <a
                                href="https://www.twitch.tv/directory/category/magic-the-gathering"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm hover:bg-purple-500/20 transition-colors border border-purple-500/20"
                            >
                                <Tv className="size-4" />
                                Browse Twitch MTG →
                            </a>
                        </div>
                    </div>
                ) : (
                    // Meta Section
                    <MetaPanel onExamineInBrew={handleExamineInBrew} />
                )}
            </div>

            {/* Save Deck Modal */}
            <SaveDeckModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={handleSaveDeck}
                deck={deck}
                defaultName={deckName}
                defaultFormat={deckSize === 40 ? 'limited' : deckSize === 99 ? 'commander' : 'standard'}
            />

            {/* Auth Modal */}
            {
                showAuthModal && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-md relative">
                            <button
                                onClick={() => setShowAuthModal(false)}
                                className="absolute top-4 right-4 text-white/40 hover:text-white/60"
                            >
                                ✕
                            </button>

                            <h2 className="text-xl font-bold text-white mb-4">
                                {authMode === 'signin' ? 'Sign In' : 'Create Account'}
                            </h2>

                            <form onSubmit={handleAuth} className="space-y-4">
                                {authMode === 'signup' && (
                                    <input
                                        type="text"
                                        placeholder="Display Name"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500/50"
                                    />
                                )}
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500/50"
                                />
                                <input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500/50"
                                />

                                {authError && (
                                    <p className="text-red-400 text-sm">{authError}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={authLoading}
                                    className="w-full py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 disabled:opacity-50 transition-colors"
                                >
                                    {authLoading ? 'Loading...' : authMode === 'signin' ? 'Sign In' : 'Create Account'}
                                </button>
                            </form>

                            <p className="mt-4 text-center text-sm text-white/50">
                                {authMode === 'signin' ? (
                                    <>Don't have an account? <button onClick={() => setAuthMode('signup')} className="text-amber-400 hover:underline">Sign Up</button></>
                                ) : (
                                    <>Already have an account? <button onClick={() => setAuthMode('signin')} className="text-amber-400 hover:underline">Sign In</button></>
                                )}
                            </p>
                        </div>
                    </div>
                )
            }

            {/* Streamer Tools Modal */}
            {
                showBroadcastModal && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="bg-slate-900 border border-purple-500/30 rounded-xl p-5 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
                            <button
                                onClick={() => setShowBroadcastModal(false)}
                                className="absolute top-3 right-3 text-white/40 hover:text-white/60 z-10"
                            >
                                <X className="size-5" />
                            </button>

                            <div className="flex items-center gap-3 mb-2">
                                <Radio className="size-5 text-purple-400" />
                                <h2 className="text-lg font-bold text-white">STREAMER SETUP GUIDE</h2>
                            </div>
                            <p className="text-white/40 text-xs mb-4">Complete these steps to get the full DECKSTR.GG experience on your stream.</p>

                            <div className="space-y-3">
                                {/* Step 1: Sign In */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-white/80 text-sm font-medium">Step 1 · Sign In to DECKSTR.GG</p>
                                        {user ? (
                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">✓ DONE</span>
                                        ) : (
                                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">REQUIRED</span>
                                        )}
                                    </div>
                                    <p className="text-white/40 text-xs mb-2">Create an account to save decks, register as a streamer, and sync across platforms.</p>
                                    {!user ? (
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                onClick={() => authService.signInOAuth('google')}
                                                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                                                title="Sign in with Google"
                                            >
                                                <svg className="size-5" viewBox="0 0 24 24">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => authService.signInOAuth('discord')}
                                                className="p-3 bg-[#5865F2]/20 hover:bg-[#5865F2]/30 rounded-lg transition-colors"
                                                title="Sign in with Discord"
                                            >
                                                <svg className="size-5 text-[#5865F2]" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => authService.signInOAuth('twitch')}
                                                className="p-3 bg-[#9146FF]/20 hover:bg-[#9146FF]/30 rounded-lg transition-colors"
                                                title="Sign in with Twitch"
                                            >
                                                <svg className="size-5 text-[#9146FF]" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => authService.signInOAuth('twitter')}
                                                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                                                title="Sign in with X"
                                            >
                                                <svg className="size-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between">
                                            <span className="text-green-400 flex items-center gap-2 text-sm">
                                                <Check className="size-4" />
                                                Signed in as {user.user_metadata?.display_name || user.email?.split('@')[0]}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Step 2: Streamer Tag */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <p className="text-white/80 text-sm font-medium mb-1">Step 2 · Your Streamer Tag</p>
                                    <p className="text-white/40 text-xs mb-2">Displayed on your OBS overlay and Twitch extension panel.</p>
                                    <input
                                        type="text"
                                        placeholder="e.g. MAGIC"
                                        value={overlayName}
                                        onChange={(e) => setOverlayName(e.target.value)}
                                        className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>

                                {/* Step 3: OBS Overlay Pack */}
                                <div className="bg-gradient-to-br from-purple-500/10 to-amber-500/5 rounded-lg p-3 border border-purple-500/20">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 3 · OBS Overlay Pack</p>
                                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">5 OVERLAYS</span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Add each as a <strong className="text-white/60">Browser Source</strong> in OBS. Click the copy button to grab the URL. Toggle visibility per scene.</p>
                                    <div className="space-y-2">
                                        {/* Arcane Console — Gameplay */}
                                        {(() => {
                                            const overlays = [
                                                {
                                                    icon: '🎮', label: 'Arcane Console', desc: 'Gameplay overlay · 3840×2160',
                                                    file: 'arcane-console.html',
                                                    params: () => [
                                                        overlayName ? `name=${encodeURIComponent(overlayName)}` : '',
                                                        user ? `user=${user.id}` : '',
                                                        twitchUsername ? `chat=${encodeURIComponent(twitchUsername)}` : '',
                                                        twitchUsername ? `arena=${encodeURIComponent(twitchUsername)}` : '',
                                                    ].filter(Boolean).join('&'),
                                                },
                                                {
                                                    icon: '⏳', label: 'Starting Soon', desc: 'Pre-stream countdown · 1920×1080',
                                                    file: 'starting-soon.html',
                                                    params: () => [
                                                        overlayName ? `name=${encodeURIComponent(overlayName)}` : '',
                                                    ].filter(Boolean).join('&'),
                                                },
                                                {
                                                    icon: '☕', label: 'Be Right Back', desc: 'Break / AFK screen · 1920×1080',
                                                    file: 'be-right-back.html',
                                                    params: () => [
                                                        overlayName ? `name=${encodeURIComponent(overlayName)}` : '',
                                                    ].filter(Boolean).join('&'),
                                                },
                                                {
                                                    icon: '🔄', label: 'Intermission', desc: 'Scene transition / just chatting · 1920×1080',
                                                    file: 'intermission.html',
                                                    params: () => [
                                                        overlayName ? `name=${encodeURIComponent(overlayName)}` : '',
                                                    ].filter(Boolean).join('&'),
                                                },
                                                {
                                                    icon: '👋', label: 'Ending Stream', desc: 'Outro / thanks for watching · 1920×1080',
                                                    file: 'ending-stream.html',
                                                    params: () => [
                                                        overlayName ? `name=${encodeURIComponent(overlayName)}` : '',
                                                    ].filter(Boolean).join('&'),
                                                },
                                            ];
                                            return overlays.map((o, i) => (
                                                <div key={i} className="flex items-center gap-2 bg-black/20 rounded-lg p-2">
                                                    <span className="text-lg w-7 text-center flex-shrink-0">{o.icon}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-white/80 text-xs font-semibold">{o.label}</div>
                                                        <div className="text-white/30 text-[10px]">{o.desc}</div>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            const p = o.params();
                                                            const url = `${window.location.origin}/overlay/${o.file}${p ? `?${p}` : ''}`;
                                                            navigator.clipboard.writeText(url);
                                                            alert(`${o.label} URL copied!`);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-500/20 text-purple-400 rounded text-[10px] font-medium hover:bg-purple-500/30 transition-colors flex-shrink-0"
                                                        title={`Copy ${o.label} URL`}
                                                    >
                                                        <Copy className="size-3" /> Copy
                                                    </button>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* Step 4: MTGA Live Tracker */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <p className="text-white/80 text-sm font-medium mb-1">Step 4 · MTGA Live Tracker</p>
                                    <p className="text-white/40 text-xs mb-2">Connect your MTG Arena game — your OBS overlay shows live card previews, zone details, and game state automatically.</p>
                                    <ArenaTracker twitchUsername={twitchUsername || overlayName} />
                                </div>

                                {/* Step 5: Twitch Extension Panel */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 5 · Twitch Extension Panel</p>
                                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                            LIVE
                                        </span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Adds a panel below your stream where viewers can browse your decklist, mana curve, and draw probabilities in real time.</p>
                                    <a
                                        href="https://dashboard.twitch.tv/extensions/tp0l50u3hzmwg4f5vwyias2s350pou-0.0.1"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full py-2 bg-[#9146FF]/20 text-[#9146FF] rounded-lg text-sm font-medium hover:bg-[#9146FF]/30 transition-colors border border-[#9146FF]/30"
                                    >
                                        <svg className="size-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
                                        </svg>
                                        Install Extension on Twitch
                                    </a>
                                </div>

                                {/* Step 6: Twitch Chat Bot */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 6 · Twitch Chat Bot Setup</p>
                                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                            LIVE
                                        </span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Adds the DECKSTR.GG bot to your Twitch chat with 25+ commands for card lookup, probability math, deck management, and match tracking.</p>
                                    <div className="space-y-2">
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-400 text-xs font-bold">①</span>
                                                <span className="text-white/60 text-xs">Go to <a href="https://twitch.tv/deckstrbot" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-semibold">twitch.tv/deckstrbot</a>, <strong className="text-amber-400">Follow</strong> the profile, and type <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">!addbot</code> in chat</span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-400 text-xs font-bold">②</span>
                                                <span className="text-white/60 text-xs">In <strong className="text-white/80">your</strong> chat, grant moderator status by typing <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">/mod deckstrbot</code> <span className="text-white/40">(bypasses slow & sub-only modes)</span></span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-400 text-xs font-bold">③</span>
                                                <span className="text-white/60 text-xs">In <strong className="text-white/80">your</strong> chat, type <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">!setdeck</code> to load a deck & <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">!deckstr</code> for all 25+ commands</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Step 7: Discord Bot */}
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 7 · Discord Bot</p>
                                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                            LIVE
                                        </span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Add the DECKSTR.GG bot to your Discord server for card lookup, deck tools, and cross-platform features.</p>
                                    <a
                                        href="https://discord.com/api/oauth2/authorize?client_id=1472607077913329684&permissions=2147483648&scope=bot%20applications.commands"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full py-2 bg-[#5865F2]/20 text-[#5865F2] rounded-lg text-sm font-medium hover:bg-[#5865F2]/30 transition-colors border border-[#5865F2]/30"
                                    >
                                        <svg className="size-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                        </svg>
                                        Add Bot to Discord Server
                                    </a>
                                </div>

                                {/* Step 7: Link Accounts */}
                                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-lg p-3 border border-purple-500/20">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 7 · Link Twitch ↔ Discord</p>
                                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium">RECOMMENDED</span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Connect your Twitch and Discord accounts so the bot can sync data between both platforms — shared decks, match history, and promo messages.</p>
                                    <div className="space-y-2">
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-cyan-400 text-xs font-bold">①</span>
                                                <span className="text-white/60 text-xs">In your Discord server, type <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">/link</code></span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-cyan-400 text-xs font-bold">②</span>
                                                <span className="text-white/60 text-xs">The bot DMs you a verification code — paste it in your Twitch chat with <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">!verify CODE</code></span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-cyan-400 text-xs font-bold">③</span>
                                                <span className="text-white/60 text-xs">Done! Your accounts are now linked across both platforms.</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Step 8: Ad Break Detection */}
                                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-lg p-3 border border-amber-500/20">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-white/80 text-sm font-medium">Step 8 · Ad Break Alerts</p>
                                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">OPTIONAL</span>
                                    </div>
                                    <p className="text-white/40 text-xs mb-3">Let the bot automatically announce when ads are running and welcome viewers back when they end. Two options:</p>
                                    <div className="space-y-2">
                                        <div className="p-2.5 bg-black/20 rounded-lg">
                                            <p className="text-white/70 text-xs font-medium mb-1">🔹 Manual (no setup needed)</p>
                                            <p className="text-white/40 text-xs">Type <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">!ad 3</code> in your chat when you run ads (number = minutes). The bot posts a heads-up and auto-posts "welcome back" when ads finish.</p>
                                        </div>
                                        <div className="p-2.5 bg-black/20 rounded-lg">
                                            <p className="text-white/70 text-xs font-medium mb-1">🔸 Automatic (requires Step 7 first)</p>
                                            <p className="text-white/40 text-xs mb-2">After linking accounts via <code className="text-amber-400 font-mono bg-black/30 px-1.5 py-0.5 rounded">/link</code>, you'll see an option to authorize ad break detection. This lets the bot detect ads automatically — no commands needed.</p>
                                            <p className="text-white/30 text-xs italic">Grants only the <code className="text-purple-300/60 font-mono bg-black/20 px-1 rounded">channel:read:ads</code> permission — the bot cannot run ads or change anything on your channel.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowBroadcastModal(false)}
                                className="mt-4 w-full py-2.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/80 text-sm rounded-lg transition-colors border border-white/10"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Brewlette Panel Modal — replaces embedded Twitch player */}
            {
                watchingStreamer && (
                    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
                        <div className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-[#0d0d1a] rounded-2xl border border-purple-500/20 shadow-2xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-amber-500/5">
                                <div className="flex items-center gap-3">
                                    {watchingStreamer.profileImageUrl ? (
                                        <img src={watchingStreamer.profileImageUrl} alt={watchingStreamer.displayName} className="w-10 h-10 rounded-full object-cover border-2 border-purple-500/50" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                            <Tv className="size-5 text-purple-400" />
                                        </div>
                                    )}
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">{watchingStreamer.displayName}</h3>
                                        <p className="text-white/50 text-xs">@{watchingStreamer.username} {watchingStreamer.streamTitle && `· ${watchingStreamer.streamTitle}`}</p>
                                    </div>
                                    {watchingStreamer.isLive && (
                                        <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-bold ml-2">
                                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                            LIVE · {watchingStreamer.viewerCount} viewers
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={watchingStreamer.twitchUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-colors border border-purple-500/30"
                                    >
                                        <ExternalLink className="size-3" />
                                        Watch on Twitch
                                    </a>
                                    <button
                                        onClick={() => setWatchingStreamer(null)}
                                        className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                                    >
                                        <X className="size-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Panel Body */}
                            <div className="flex-1 overflow-y-auto p-4">
                                {streamerPanelLoading ? (
                                    <div className="flex items-center justify-center h-64">
                                        <Loader2 className="size-8 text-purple-400 animate-spin" />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Left Column: Active Deck + Score */}
                                        <div className="space-y-4">
                                            {/* Now Playing */}
                                            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Play className="size-4 text-green-400" />
                                                    <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Now Playing</h4>
                                                </div>
                                                {streamerActiveDeck ? (
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h5 className="text-lg font-bold text-white">{streamerActiveDeck.name || 'Unnamed Deck'}</h5>
                                                            <div className="flex items-center gap-1">
                                                                {(streamerActiveDeck.colors || streamerActiveDeck.color_identity || []).map((c: string) => (
                                                                    <ManaSymbol key={c} symbol={c} size="sm" />
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {streamerActiveDeck.submitter && (
                                                            <p className="text-amber-400 text-xs mb-2">Submitted by <span className="font-semibold">{streamerActiveDeck.submitter}</span></p>
                                                        )}
                                                        {/* Card count summary */}
                                                        <div className="flex items-center gap-3 text-white/50 text-xs">
                                                            <span>{Object.values(streamerActiveDeck.cards || {}).reduce((a: number, b: any) => a + b, 0)} spells</span>
                                                            <span>·</span>
                                                            <span>{Object.values(streamerActiveDeck.lands || {}).reduce((a: number, b: any) => a + b, 0)} lands</span>
                                                            {streamerActiveDeck.sideboard && Object.keys(streamerActiveDeck.sideboard).length > 0 && (
                                                                <><span>·</span><span>{Object.values(streamerActiveDeck.sideboard).reduce((a: number, b: any) => a + b, 0)} SB</span></>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-white/30 text-sm py-4 text-center">
                                                        <Sword className="size-6 mx-auto mb-2 opacity-30" />
                                                        No deck loaded
                                                    </div>
                                                )}
                                            </div>

                                            {/* Win/Loss Record */}
                                            {streamerScore && (
                                                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                                    <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Session Record</h4>
                                                    <div className="flex items-center gap-6">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-black text-green-400">{streamerScore.wins}</div>
                                                            <div className="text-[10px] text-white/40 uppercase">Wins</div>
                                                        </div>
                                                        <div className="text-white/20 text-2xl font-thin">–</div>
                                                        <div className="text-center">
                                                            <div className="text-2xl font-black text-red-400">{streamerScore.losses}</div>
                                                            <div className="text-[10px] text-white/40 uppercase">Losses</div>
                                                        </div>
                                                        {streamerScore.draws > 0 && (
                                                            <>
                                                                <div className="text-white/20 text-2xl font-thin">–</div>
                                                                <div className="text-center">
                                                                    <div className="text-2xl font-black text-white/60">{streamerScore.draws}</div>
                                                                    <div className="text-[10px] text-white/40 uppercase">Draws</div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Submit Your Deck */}
                                            <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/10 rounded-xl p-4 border border-amber-500/20">
                                                <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">Submit to Brewlette</h4>
                                                <p className="text-white/40 text-xs mb-3">Submit your currently loaded deck to this streamer's brew queue. Costs 3 Sparks.</p>
                                                <button
                                                    onClick={handleSubmitToBrewlette}
                                                    disabled={submittingDeck || !user || deck.length === 0}
                                                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-semibold hover:bg-amber-500/30 transition-colors border border-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    {submittingDeck ? (
                                                        <Loader2 className="size-4 animate-spin" />
                                                    ) : (
                                                        <Send className="size-4" />
                                                    )}
                                                    {submittingDeck ? 'Submitting...' : `Submit Deck (3 ⚡)`}
                                                </button>
                                                {!user && <p className="text-white/30 text-[10px] text-center mt-1">Sign in to submit</p>}
                                                {user && deck.length === 0 && <p className="text-white/30 text-[10px] text-center mt-1">Load a deck in the BREW tab first</p>}
                                            </div>
                                        </div>

                                        {/* Right Column: Brew Queue */}
                                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <BookOpen className="size-4 text-purple-400" />
                                                    <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Brew Queue</h4>
                                                </div>
                                                <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-white/50">
                                                    {streamerQueue.filter(q => q.status === 'waiting').length} waiting
                                                </span>
                                            </div>
                                            {streamerQueue.length === 0 ? (
                                                <div className="text-white/30 text-sm py-8 text-center">
                                                    <BookOpen className="size-8 mx-auto mb-2 opacity-20" />
                                                    Queue is empty — be the first to submit!
                                                </div>
                                            ) : (
                                                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                                    {streamerQueue.map((entry, idx) => (
                                                        <div
                                                            key={entry.id}
                                                            className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${entry.status === 'selected'
                                                                ? 'bg-green-500/10 border border-green-500/30'
                                                                : 'bg-white/[0.03] hover:bg-white/[0.06] border border-transparent'
                                                                }`}
                                                        >
                                                            {/* Position / Status */}
                                                            <div className="min-w-[36px] text-center">
                                                                {entry.status === 'selected' ? (
                                                                    <span className="text-[10px] font-bold text-green-400 uppercase leading-tight">NOW<br />PLAYING</span>
                                                                ) : (
                                                                    <>
                                                                        <div className="text-[10px] text-white/30 font-semibold">#{idx + 1}</div>
                                                                        <div className="text-base font-black text-amber-400" style={{ textShadow: '0 0 8px rgba(245,158,11,0.4)' }}>
                                                                            {entry.sparks || 0}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {/* Deck Info */}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-white truncate">{entry.deck_name || 'Unnamed'}</div>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-purple-400 text-xs font-semibold">@{entry.display_name || '???'}</span>
                                                                    <div className="flex items-center gap-0.5">
                                                                        {(entry.deck_colors || []).map((c: string) => (
                                                                            <ManaSymbol key={c} symbol={c} size="sm" />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {/* Spark icon */}
                                                            <div className="text-amber-400/50">
                                                                <Zap className="size-3.5" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-3 border-t border-white/10 bg-white/[0.02]">
                                <div className="flex items-center gap-2 text-white/30 text-xs">
                                    <Zap className="size-3 text-amber-400" />
                                    Your balance: <span className="font-semibold text-amber-400">{sparkBalance} ⚡</span>
                                </div>
                                <p className="text-white/30 text-xs">Panel refreshes every 15s</p>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Deck Playtester */}
            {
                showPlaytester && playtesterDeck && (
                    <DeckPlaytester
                        deck={playtesterCards}
                        sideboard={Object.entries(playtesterDeck.sideboard || {}).map(([name, quantity]) => ({
                            name,
                            quantity,
                            cmc: 0,
                            isLand: false,
                            colors: [],
                        }))}
                        plans={playtesterDeck.plans}
                        deckName={playtesterDeck.name}
                        mode={playtesterMode}
                        onClose={() => {
                            setShowPlaytester(false);
                            setPlaytesterDeck(null);
                            setPlaytesterCards([]);
                        }}
                    />
                )
            }
            {/* Spark Toast Notifications */}
            {sparkToasts.length > 0 && (
                <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2">
                    {sparkToasts.map((toast) => (
                        <div
                            key={toast.id}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl border backdrop-blur-md animate-[slideIn_0.3s_ease-out] ${toast.type === 'earn'
                                ? 'bg-amber-900/80 border-amber-500/40 text-amber-100'
                                : 'bg-red-900/80 border-red-500/40 text-red-100'
                                }`}
                            style={{
                                animation: 'slideIn 0.3s ease-out'
                            }}
                        >
                            <Zap className={`size-5 ${toast.type === 'earn' ? 'text-amber-400' : 'text-red-400'
                                }`} />
                            <div>
                                <span className="text-sm font-medium">{toast.message}</span>
                                {toast.amount > 0 && (
                                    <span className={`ml-2 text-sm font-bold ${toast.type === 'earn' ? 'text-amber-300' : 'text-red-300'
                                        }`}>
                                        {toast.type === 'earn' ? '+' : '-'}{toast.amount} ⚡
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Spark toast slide-in animation */}
            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>
        </div >
    );
}
